// /programs/speed — upload a video and change its speed, resolution/quality,
// and container (확장자). Conversion runs server-side via ffmpeg.
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";

const SPEED_PRESETS = [0.5, 1, 1.25, 1.5, 2];
const RESOLUTIONS: { value: string; label: string }[] = [
  { value: "original", label: "원본" },
  { value: "1080", label: "1080p" },
  { value: "720", label: "720p" },
  { value: "480", label: "480p" },
  { value: "360", label: "360p" },
];
const QUALITIES: { value: string; label: string }[] = [
  { value: "high", label: "고화질" },
  { value: "medium", label: "표준" },
  { value: "low", label: "저용량" },
];
const FORMATS: { value: string; label: string }[] = [
  { value: "mp4", label: "MP4" },
  { value: "webm", label: "WebM" },
  { value: "mov", label: "MOV" },
  { value: "mkv", label: "MKV" },
  { value: "avi", label: "AVI" },
  { value: "gif", label: "GIF" },
];

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

type Phase = "idle" | "uploading" | "processing" | "done";

export default function VideoSpeedPage() {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [speed, setSpeed] = useState(1);
  const [resolution, setResolution] = useState("original");
  const [quality, setQuality] = useState("medium");
  const [fmt, setFmt] = useState("mp4");

  const [phase, setPhase] = useState<Phase>("idle");
  const [uploadPct, setUploadPct] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultName, setResultName] = useState<string>("");
  const [resultSize, setResultSize] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs on change/unmount to avoid leaks.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => () => { if (resultUrl) URL.revokeObjectURL(resultUrl); }, [resultUrl]);

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("영상 파일만 업로드할 수 있습니다"); return; }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (resultUrl) { URL.revokeObjectURL(resultUrl); setResultUrl(null); }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setPhase("idle");
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setFile(null); setPreviewUrl(null); setResultUrl(null); setResultName("");
    setPhase("idle"); setUploadPct(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const convert = async () => {
    if (!file) return;
    setPhase("uploading"); setUploadPct(0);
    if (resultUrl) { URL.revokeObjectURL(resultUrl); setResultUrl(null); }

    const form = new FormData();
    form.append("file", file);
    form.append("speed", String(speed));
    form.append("resolution", resolution);
    form.append("quality", quality);
    form.append("fmt", fmt);

    try {
      const res = await api.post("/programs/video/convert", form, {
        responseType: "blob",
        timeout: 0, // conversion can take minutes
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadPct(pct);
            if (pct >= 100) setPhase("processing");
          }
        },
      });

      // Derive download filename from Content-Disposition, else construct one.
      const cd = res.headers["content-disposition"] as string | undefined;
      const base = file.name.replace(/\.[^.]+$/, "");
      let name = `${base}_converted.${fmt}`;
      const m = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      if (m) name = decodeURIComponent(m[1]);

      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      setResultUrl(url); setResultName(name); setResultSize(blob.size);
      setPhase("done");
      toast.success("변환 완료", name);
    } catch (e: any) {
      setPhase("idle");
      // Error body comes back as a blob (responseType=blob) — read it as text.
      let msg = "변환에 실패했습니다";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          msg = JSON.parse(txt)?.error?.message ?? msg;
        }
      } catch { /* keep default */ }
      toast.error("변환 실패", msg);
    }
  };

  const download = () => {
    if (!resultUrl) return;
    const a = document.createElement("a");
    a.href = resultUrl; a.download = resultName;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const busy = phase === "uploading" || phase === "processing";

  return (
    <div style={{ padding: "24px 28px 48px", maxWidth: 860, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, display: "flex", alignItems: "center", gap: 9 }}>
          <span>🎬</span> 영상 변환
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-3)" }}>
          영상을 업로드하고 재생 속도 · 화질(해상도) · 확장자를 변경하세요.
        </p>
      </div>

      {/* Drop zone / preview */}
      {!file ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] ?? null); }}
          style={{
            border: `2px dashed ${dragOver ? "var(--p-500)" : "var(--border-strong, #D1D5DB)"}`,
            borderRadius: 14, background: dragOver ? "color-mix(in srgb, var(--p-500) 7%, transparent)" : "var(--bg-0)",
            padding: "56px 24px", textAlign: "center", cursor: "pointer", transition: "all .15s",
          }}
        >
          <div style={{ fontSize: 44, marginBottom: 12 }}>📤</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>영상을 끌어다 놓거나 클릭하여 업로드</div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>MP4 · MOV · WebM · MKV · AVI — 최대 1GB</div>
          <input ref={inputRef} type="file" accept="video/*" style={{ display: "none" }}
                 onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
        </div>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: 14, background: "var(--bg-0)", overflow: "hidden" }}>
          {previewUrl && (
            <video src={previewUrl} controls style={{ width: "100%", maxHeight: 380, background: "#000", display: "block" }} />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
            <span style={{ fontSize: 20 }}>🎞️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
              <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{fmtBytes(file.size)}</div>
            </div>
            <button className="t-btn t-btn-sm" onClick={reset} disabled={busy}>다른 영상</button>
          </div>
        </div>
      )}

      {/* Options — always visible so the page reads as a complete tool even
          before a file is chosen; the convert button stays disabled until then. */}
      {(
        <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
          {/* Speed */}
          <Section title="재생 속도" hint={`${speed}배`}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {SPEED_PRESETS.map((s) => (
                <button key={s} onClick={() => setSpeed(s)} disabled={busy}
                  style={chip(speed === s)}>
                  {s === 1 ? "원본 (1x)" : `${s}x`}
                </button>
              ))}
            </div>
            <input type="range" min={0.25} max={4} step={0.25} value={speed} disabled={busy}
                   onChange={(e) => setSpeed(Number(e.target.value))}
                   style={{ width: "100%", accentColor: "var(--p-500)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--text-3)", marginTop: 2 }}>
              <span>0.25x (느리게)</span><span>4x (빠르게)</span>
            </div>
          </Section>

          {/* Resolution + Quality */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Section title="해상도">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {RESOLUTIONS.map((r) => (
                  <button key={r.value} onClick={() => setResolution(r.value)} disabled={busy}
                    style={chip(resolution === r.value)}>{r.label}</button>
                ))}
              </div>
            </Section>
            <Section title="압축 품질">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {QUALITIES.map((q) => (
                  <button key={q.value} onClick={() => setQuality(q.value)} disabled={busy || fmt === "gif"}
                    style={chip(quality === q.value, fmt === "gif")}>{q.label}</button>
                ))}
              </div>
            </Section>
          </div>

          {/* Format */}
          <Section title="확장자 (출력 형식)">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {FORMATS.map((f) => (
                <button key={f.value} onClick={() => setFmt(f.value)} disabled={busy}
                  style={chip(fmt === f.value)}>{f.label}</button>
              ))}
            </div>
            {fmt === "gif" && (
              <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 8 }}>
                GIF은 소리가 제거되고 12fps · 너비 480px(해상도 선택 시 해당 높이)로 변환됩니다.
              </div>
            )}
          </Section>

          {/* Action */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
            <button className="t-btn t-btn-primary" onClick={convert} disabled={busy || !file}
              style={{ minWidth: 140, height: 42 }}>
              {phase === "uploading" ? `업로드 중… ${uploadPct}%`
                : phase === "processing" ? "변환 중…"
                : "변환 시작"}
            </button>
            {!file && !busy && (
              <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>먼저 위에서 영상을 업로드하세요.</span>
            )}
            {busy && (
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: "var(--panel)", overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 4, background: "var(--p-500)",
                  width: phase === "processing" ? "100%" : `${uploadPct}%`,
                  transition: "width .2s",
                  opacity: phase === "processing" ? 0.5 : 1,
                  animation: phase === "processing" ? "pulse 1.2s ease-in-out infinite" : "none",
                }} />
                <style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style>
              </div>
            )}
          </div>
          {phase === "processing" && (
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>
              서버에서 영상을 처리하고 있습니다. 영상 길이에 따라 수 분이 걸릴 수 있습니다…
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {phase === "done" && resultUrl && (
        <div style={{ marginTop: 18, border: "1px solid var(--ok, #16A34A)", borderRadius: 14, background: "color-mix(in srgb, var(--ok, #16A34A) 7%, transparent)", padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>변환 완료</div>
              <div style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {resultName} · {fmtBytes(resultSize)}
              </div>
            </div>
            <button className="t-btn t-btn-primary" onClick={download}>⬇ 다운로드</button>
          </div>
          <video src={resultUrl} controls style={{ width: "100%", maxHeight: 320, background: "#000", borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

// ─── small helpers ──────────────────────────────────────────────────────────────

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--bg-0)", padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{title}</span>
        {hint && <span style={{ fontSize: 12, fontWeight: 700, color: "var(--p-500)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function chip(active: boolean, disabled = false): React.CSSProperties {
  return {
    padding: "6px 14px", borderRadius: 20, fontSize: 12.5, cursor: disabled ? "not-allowed" : "pointer",
    border: `1.5px solid ${active ? "var(--p-500)" : "var(--border)"}`,
    background: active ? "color-mix(in srgb, var(--p-500) 12%, transparent)" : "transparent",
    color: active ? "var(--p-600, #4f46e5)" : "var(--text-2)",
    fontWeight: active ? 700 : 500,
    opacity: disabled ? 0.4 : 1,
  };
}
